import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Wrench, DownloadSimple, ArrowsClockwise, Sparkle, Percent } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ToolsPanel = () => {
  const [busy, setBusy] = useState(null);
  const [refreshResult, setRefreshResult] = useState(null);
  const [margin, setMargin] = useState('35');

  const downloadBackup = async () => {
    setBusy('backup');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/backup`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${new Date().toISOString().slice(0, 16).replace(':', '')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Backup descargado');
    } catch (e) {
      toast.error('Error al generar backup');
    } finally { setBusy(null); }
  };

  const refreshOrders = async () => {
    setBusy('refresh');
    setRefreshResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/tools/refresh-orders`, {}, { withCredentials: true });
      setRefreshResult(data);
      toast.success(`Actualizados ${data.updated}/${data.checked} pedidos`);
    } catch (e) {
      const m = e?.response?.data?.detail || 'Error';
      toast.error(typeof m === 'string' ? m : JSON.stringify(m));
    } finally { setBusy(null); }
  };

  const clearBrowserCache = () => {
    try {
      // Solo limpia localStorage/sessionStorage de esta sesión — cookies http-only no se tocan.
      const keep = ['am_device_id']; // preservar device analytics
      const values = {};
      keep.forEach((k) => (values[k] = localStorage.getItem(k)));
      localStorage.clear();
      sessionStorage.clear();
      Object.entries(values).forEach(([k, v]) => v && localStorage.setItem(k, v));
      toast.success('Caché del navegador limpiada');
    } catch (e) {
      toast.error('No se pudo limpiar');
    }
  };

  const saveMargin = async () => {
    const pct = parseFloat(margin);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Margen debe estar entre 0 y 100');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/stats/margin`, { estimated_margin_pct: pct }, { withCredentials: true });
      toast.success('Margen guardado');
    } catch (e) {
      toast.error('Error al guardar margen');
    }
  };

  return (
    <div className="space-y-5" data-testid="tools-panel">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Wrench size={20} weight="duotone" className="text-primary" /> Herramientas
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border-border" data-testid="tool-backup">
          <div className="flex items-center gap-3 mb-2">
            <DownloadSimple size={22} weight="duotone" className="text-primary" />
            <h3 className="font-semibold">Backup completo</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Descarga un JSON con usuarios, pedidos, servicios, configuración y logs.
            Guardalo en un lugar seguro (Google Drive, disco externo).
          </p>
          <Button onClick={downloadBackup} disabled={busy === 'backup'} data-testid="backup-button">
            {busy === 'backup' ? 'Generando...' : 'Descargar backup'}
          </Button>
        </Card>

        <Card className="p-5 border-border" data-testid="tool-refresh">
          <div className="flex items-center gap-3 mb-2">
            <ArrowsClockwise size={22} weight="duotone" className="text-primary" />
            <h3 className="font-semibold">Sincronizar pedidos con SMM</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Consulta el estado de todos los pedidos en proceso al panel SMM y actualiza sus estados en la base de datos.
          </p>
          <Button onClick={refreshOrders} disabled={busy === 'refresh'} data-testid="refresh-button">
            {busy === 'refresh' ? 'Sincronizando...' : 'Sincronizar ahora'}
          </Button>
          {refreshResult && (
            <div className="mt-3 text-xs text-muted-foreground">
              Chequeados: <strong>{refreshResult.checked}</strong> · Actualizados: <strong>{refreshResult.updated}</strong> · Errores: <strong>{refreshResult.errors}</strong>
            </div>
          )}
        </Card>

        <Card className="p-5 border-border" data-testid="tool-cache">
          <div className="flex items-center gap-3 mb-2">
            <Sparkle size={22} weight="duotone" className="text-primary" />
            <h3 className="font-semibold">Limpiar caché del navegador</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Borra el localStorage y sessionStorage del panel admin (conserva el ID de dispositivo para analytics).
          </p>
          <Button variant="outline" onClick={clearBrowserCache} data-testid="cache-button">
            Limpiar caché
          </Button>
        </Card>

        <Card className="p-5 border-border" data-testid="tool-margin">
          <div className="flex items-center gap-3 mb-2">
            <Percent size={22} weight="duotone" className="text-primary" />
            <h3 className="font-semibold">Margen para ganancias estimadas</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Se usa en el dashboard para estimar ganancia total (ingresos × margen%).
          </p>
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="margin-input">Margen %</Label>
              <Input id="margin-input" type="number" min="0" max="100" value={margin} onChange={(e) => setMargin(e.target.value)} data-testid="margin-input" />
            </div>
            <Button onClick={saveMargin} data-testid="margin-save">Guardar</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
